import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MonitoringDashboardModal.module.css';

/**
 * 탭별 고정 그리드 레이아웃 선택기.
 * - 대칭: 1x1, 1x2, 2x1, 2x2
 * - 비대칭: 1L2R (좌측 큰 패널 + 우측 2분할), 2L1R (좌측 2분할 + 우측 큰 패널)
 * - "자동" (value=null) 은 auto-fit 모드.
 */
const getLayoutOptions = (t) => [
  { value: null,   label: t('monitoring:lay.auto'),  cols: 0, rows: 0, ariaLabel: t('monitoring:lay.autoFit') },
  { value: '1x1',  cols: 1, rows: 1, ariaLabel: t('monitoring:lay.1x1') },
  { value: '1x2',  cols: 2, rows: 1, ariaLabel: t('monitoring:lay.1x2') },
  { value: '2x1',  cols: 1, rows: 2, ariaLabel: t('monitoring:lay.2x1') },
  { value: '1L2R', cols: 2, rows: 2,
    areas: '"L TR" "L BR"',
    cells: ['L', 'TR', 'BR'],
    ariaLabel: t('monitoring:lay.rightSplit') },
  { value: '2L1R', cols: 2, rows: 2,
    areas: '"TL R" "BL R"',
    cells: ['TL', 'BL', 'R'],
    ariaLabel: t('monitoring:lay.leftSplit') },
  { value: '2x2',  cols: 2, rows: 2, ariaLabel: t('monitoring:lay.2x2') },
];

function LayoutIcon({ cols, rows, areas, cells }) {
  if (cols === 0 || rows === 0) return null;
  const style = {
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: `repeat(${rows}, 1fr)`,
  };
  if (areas) style.gridTemplateAreas = areas;
  return (
    <span className={styles.layoutIcon} style={style}>
      {cells
        ? cells.map((area, i) => <span key={i} style={{ gridArea: area }} />)
        : Array.from({ length: cols * rows }).map((_, i) => <span key={i} />)}
    </span>
  );
}

const LayoutSelector = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  // value undefined 또는 null 모두 "자동"으로 간주
  const normalized = value == null ? null : value;
  return (
    <div
      className={styles.layoutSelector}
      role="radiogroup"
      aria-label={t('monitoring:lay.title')}
    >
      {getLayoutOptions(t).map(opt => {
        const isActive = normalized === opt.value;
        const isAuto = opt.value === null;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={opt.ariaLabel}
            title={opt.ariaLabel}
            disabled={disabled}
            onClick={() => { if (!disabled) onChange(opt.value); }}
            className={[
              styles.layoutOption,
              isAuto ? styles.layoutOptionAuto : '',
              isActive ? styles.layoutOptionActive : '',
            ].filter(Boolean).join(' ')}
          >
            {isAuto
              ? opt.label
              : <LayoutIcon cols={opt.cols} rows={opt.rows} areas={opt.areas} cells={opt.cells} />}
          </button>
        );
      })}
    </div>
  );
};

export default LayoutSelector;
